import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

import { Helmet } from "react-helmet-async";
import {
    Button,
    Card,
    Col,
    Container,
    Form,
    OverlayTrigger,
    Row,
    Tooltip,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import DynamicTable from "components/ui/DynamicTable";
import * as rolesApi from "@api/rolesApi";
import NotyfContext from "contexts/NotyfContext";
import { DeleteModal } from "components/DeleteModal";
import { LoadingContext } from "App";
import { formatConfigFields } from "utils/utilities";
import {
    faPen,
    faPlus,
    faSearch,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const RerenderContextRoles = createContext();

export const Roles = () => {
    const navigate = useNavigate();
    const notyf = useContext(NotyfContext);
    const location = useLocation();
    const [isLoadingActive, setIsLoadingActive] = useContext(LoadingContext);
    const [width] = useState(window.innerWidth);
    let isDesktop = width > 1400;

    //
    // States
    //

    const [roles, setRoles] = useState([]);
    const [rerender, setRerender] = useState([]);
    const [modalInfo, setModalInfo] = useState({
        id: null,
        notifMsg: "",
        open: false,
        severity: "danger",
    });
    const [searchValue, setSearchValue] = useState({
        name: "",
        slug: "",
        description: "",
    });
    const [configColumn, setFieldColumn] = useState([]);

    const actionField = {
        Header: "Actions",
        accessor: "actions",
        width: isDesktop ? 70 : 120,
        Cell: ({ cell }) => {
            return (
                <>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Edit role</Tooltip>}
                    >
                        <span>
                            <FontAwesomeIcon
                                icon={faPen}
                                className="align-middle me-1"
                                onClick={() =>
                                    navigate(
                                        location.pathname +
                                            "/" +
                                            cell.row.original.id
                                    )
                                }
                                size="lg"
                            />
                        </span>
                    </OverlayTrigger>
                    <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip>Delete role</Tooltip>}
                    >
                        <span>
                            <FontAwesomeIcon
                                icon={faTrash}
                                className="align-middle me-1"
                                onClick={() => {
                                    openDeleteModal(cell.row.original.id);
                                }}
                                size="lg"
                            />
                        </span>
                    </OverlayTrigger>
                </>
            );
        },
    };

    //
    // Functions
    //

    const deleteRole = useCallback(
        async (id) => {
            try {
                const response = await rolesApi.deleteRole(id);
                if (response.status === 200) {
                    notyf.open({
                        type: "success",
                        message: response.data.message,
                    });
                    setRerender(!rerender);
                }
            } catch (error) {
                notyf.open({
                    type: "danger",
                    message: "Something went wrong with the server",
                });
            }
        },
        [notyf, rerender]
    );

    const openDeleteModal = (originalId) => {
        setModalInfo({
            id: originalId,
            notifMsg: "Are you sure you want to delete this item?",
            open: true,
            severity: "primary",
        });
    };

    const getRoles = useCallback(
        async (param = null) => {
            try {
                setIsLoadingActive(true);
                const response = await rolesApi.getRoles(param);

                if (response.status === 200) {
                    setRoles(response.data);
                    setIsLoadingActive(false);
                }
            } catch (error) {
                setIsLoadingActive(false);
                notyf.open({
                    type: "danger",
                    message: "Something went wrong with the server",
                });
            }
        },
        [notyf, setIsLoadingActive]
    );

    const getConfig = useCallback(
        async (param = null) => {
            try {
                const response = await rolesApi.getRolesConfig(param);
                setFieldColumn(response.data?.data);
            } catch (error) {
                notyf.open({
                    type: "danger",
                    message: "Something went wrong with the server",
                });
            }
        },
        [notyf]
    );

    const search = () => {
        getRoles(searchValue);
    };

    //
    // UseEffects
    //

    useEffect(() => {
        getRoles();
        getConfig();
    }, [getRoles, rerender, getConfig]);

    return (
        <RerenderContextRoles.Provider value={[setRerender, rerender]}>
            <Helmet title="Roles" />
            <Container fluid className="p-0">
                <h4 className="h4 mb-3">Roles</h4>
                <Card>
                    <Card.Header>
                        <Row>
                            <Col md={10} sm={10} xs={10}>
                                <Row>
                                    <Col md={3} sm={5} xs={5}>
                                        <Form.Control
                                            type="text"
                                            className="d-inline-block form-box"
                                            placeholder="Name"
                                            onChange={(e) =>
                                                setSearchValue({
                                                    ...searchValue,
                                                    name: e.target.value,
                                                })
                                            }
                                        />
                                    </Col>
                                    <Col md={3} sm={5} xs={5}>
                                        <Form.Control
                                            type="text"
                                            className="d-inline-block form-box"
                                            placeholder="Slug"
                                            onChange={(e) =>
                                                setSearchValue({
                                                    ...searchValue,
                                                    slug: e.target.value,
                                                })
                                            }
                                        />
                                    </Col>
                                    <Col md={3} sm={5} xs={5}>
                                        <Form.Control
                                            type="text"
                                            className="d-inline-block form-box"
                                            placeholder="Description"
                                            onChange={(e) =>
                                                setSearchValue({
                                                    ...searchValue,
                                                    description: e.target.value,
                                                })
                                            }
                                        />
                                    </Col>
                                    <Col md={3} sm={2} xs={2}>
                                        <Button
                                            variant="primary"
                                            className="me-1 mb-1 form-box"
                                            onClick={search}
                                        >
                                            <FontAwesomeIcon icon={faSearch} />
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={2} sm={2} xs={2}>
                                <h1 className="h3 mb-3">
                                    <Button
                                        variant="primary"
                                        className="float-end mb-2 form-box"
                                        onClick={() =>
                                            navigate(`/settings/roles/create`)
                                        }
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </Button>
                                </h1>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body className="pt-1">
                        {!isLoadingActive && configColumn && roles.data && (
                            <DynamicTable
                                dataPagination={roles}
                                columns={formatConfigFields(
                                    configColumn,
                                    actionField
                                )}
                                className="table-layout-fixed"
                            />
                        )}
                        <DeleteModal
                            modalInfo={modalInfo}
                            setModalInfo={setModalInfo}
                            api={deleteRole}
                        />
                    </Card.Body>
                </Card>
            </Container>
        </RerenderContextRoles.Provider>
    );
};
